/**
 * Report export.
 *
 * Markdown and JSON are produced in the browser from the already-fetched
 * report, which keeps the Worker out of the loop and works offline. The
 * entitlement check still happens server-side: the Pro-only JSON button is only
 * rendered when /me reports the entitlement, and the server remains the
 * authority for anything it actually serves.
 */

import { reportToJson, reportToMarkdown } from '@core/report';
import type { DiagnosisReport } from '@core/types';

export type ExportFormat = 'markdown' | 'json';

export function buildExport(report: DiagnosisReport, format: ExportFormat): string {
  return format === 'json' ? reportToJson(report) : reportToMarkdown(report);
}

export function downloadExport(
  report: DiagnosisReport,
  format: ExportFormat,
  filenameBase = 'api-rescue-desk-report',
): void {
  const content = buildExport(report, format);
  const extension = format === 'json' ? 'json' : 'md';
  const mime = format === 'json' ? 'application/json' : 'text/markdown';
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filenameBase}-${new Date().toISOString().slice(0, 10)}.${extension}`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
